import { Bell, Shield, Store, Users } from 'lucide-react';
import { useOrganization, useUser } from '@clerk/clerk-react';

import { useShop } from '@/hooks/useShop';
import { useShopPath } from '@/hooks/useShopPath';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { useWeeklyInsightsPreference } from '@/hooks/useWeeklyInsightsPreference';
import { SettingsCardList } from './components/SettingsCardList';

function teamDetail(membersCount, isLoaded) {
	if (!isLoaded || membersCount == null) return 'Loading members…';
	return `${membersCount} ${membersCount === 1 ? 'user' : 'users'}`;
}

function addStoreDetail(count) {
	return `${count} ${count === 1 ? 'store' : 'stores'}`;
}

function notificationsDetail({ enabled, loading }) {
	if (loading) return 'Loading preferences…';
	return enabled ? 'Weekly digest · on' : 'Weekly digest · off';
}

function securityDetail(user, isLoaded) {
	if (!isLoaded || !user) return 'Loading security…';
	// Clerk's user model exposes `twoFactorEnabled` (any 2FA factor) +
	// `passwordEnabled` (has a password vs SSO-only). We surface a
	// human-readable summary so the card matches the user's real state
	// without duplicating Clerk's UserProfile UI on the landing page.
	const twoFactor = user.twoFactorEnabled;
	const password = user.passwordEnabled;
	const parts = [];
	parts.push(password ? 'Password set' : 'No password');
	parts.push(twoFactor ? '2FA on' : '2FA off');
	return parts.join(' · ');
}

export function AccountLanding() {
	const { shopPath } = useShopPath();
	const { shop, shops } = useShop();
	const { organization, isLoaded: orgLoaded } = useOrganization();
	const { user, isLoaded: userLoaded } = useUser();
	const { billing } = useBillingStatus(shop?.id);
	const { enabled: weeklyEnabled, loading: weeklyLoading } = useWeeklyInsightsPreference();
	const membersCount = organization?.membersCount;
	// Mirror the sidebar gate that previously hosted this link: only an org with
	// an active-ish subscription can add (and be billed for) another location.
	const canAddStore = ['active', 'trialing', 'past_due'].includes(billing?.status);

	const items = [
		{
			icon: Users,
			label: 'Team & access',
			detail: teamDetail(membersCount, orgLoaded),
			to: shopPath('/settings/account/team')
		},
		...(canAddStore
			? [
					{
						icon: Store,
						label: 'Add another store',
						detail: addStoreDetail(shops.length),
						to: shopPath('/add-store')
					}
				]
			: []),
		{
			icon: Bell,
			label: 'Notifications',
			detail: notificationsDetail({ enabled: weeklyEnabled, loading: weeklyLoading }),
			to: shopPath('/settings/account/notifications')
		},
		{
			icon: Shield,
			label: 'Security',
			detail: securityDetail(user, userLoaded),
			to: shopPath('/settings/account/security')
		}
	];

	return <SettingsCardList items={items} />;
}
